import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';

//Styles
import Styles from './detail-marketing.module.scss'

//Components
import Modal from 'components/modal'
import Loader from 'components/molecules/Loader';
import Button from 'components/atomic/button-v2'
import { FrontCardV2, BackCardV2 } from 'pages/dashboard/marketing/components/id-card-v2';

//Assets
import { ReactComponent as CrossIcon } from "assets/cross.svg";
import { ReactComponent as CrossBlackIcon } from "assets/cross-black.svg";
import { ReactComponent as DoubleCheck } from "assets/double-check.svg";
import { ReactComponent as Pencil } from "assets/pencil-line-grey.svg";
import { ReactComponent as Gallery } from "assets/gallery.svg";
import { ReactComponent as IdentificationCard } from "assets/identification-card.svg";

//Hooks
import useOutsideAlerter from 'hooks/useOutsideAlerter'

//Actions
import {
  updateColleague
} from "actions";

const PORTAL_URL = process.env.REACT_APP_PORTAL_URL;

const DefaultImage = "https://d3hq2iy4ol95dk.cloudfront.net/assets/default/image-90efdb05b35f99324bb28b2d9827c138c2574cc8fafbeec6e37fdeea501fb6e6.png"

const Input = ({ label, ...props }) => {
  return (
    <div className={`${Styles['input-wrapper']}`}>
      <p className={Styles['label']}>{label}</p>
      <input className={Styles["input"]} {...props} />
    </div>
  )
}

const InputPhone = ({ label, disabled, ...props }) => {
  return (
    <div className={`${Styles['input-wrapper']}`}>
      <p className={Styles['label']}>{label}</p>
      <div className={`${Styles['input-phone']} ${!!disabled && Styles["disabled"]}`}>
        <span className={`mr-2 ${Styles['prefix']}`}>+62</span>
        <input disabled={disabled} className='w-full' {...props} />
      </div>
    </div>
  )
}

function removePrefix(number) {
  if (number.startsWith("+62")) {
    return number.slice(3);
  } else if (number.startsWith("0")) {
    return number.slice(1);
  } else {
    return number;
  }
}

const DetailMarketing = ({ data, onClose }) => {
  const frontCardRef = useRef(null);
  const backCardRef = useRef(null);
  const modalIdCardRef = useRef(null);

  const [qrCodeUrl, setQrCodeUrl] = useState("");

  const {
    image,
    name,
    username,
    email,
    phone,
    id,
    imageId
  } = useMemo(() => ({
    image: data?.user_image?.public?.url ?? '',
    name: data?.full_name ?? '',
    username: data?.username ?? '',
    email: data?.email ?? '',
    phone: !!(typeof data?.phone === "string") ? removePrefix(data.phone) : '',
    id: data?.id,
    imageId: data?.user_image?.id
  }), [data]);

  const [form, setForm] = useState({
    full_name: name,
    username,
    email,
    phone,
  })

  const [currentName, setCurrentName] = useState(data?.full_name ?? "")
  const [isOpenIdCard, setIsOpenIdCard] = useState(false)

  const onChangeForm = (e) => {
    const { name, value } = e.target
    const newForm = { ...form }

    if (name === 'phone') {
      if (/^\d*$/.test(value) && !/^0/.test(value)) {
        newForm[name] = value
      }
    } else {
      newForm[name] = value
    }

    if (name === "email") {
      newForm[name] = value?.replace(" ", "")?.toLowerCase()
    }

    setForm(newForm)
  }

  const [loading, setLoading] = useState(false)

  //Update photo
  const [currentPhoto, setCurrentPhoto] = useState(null)

  const onDrop = useCallback(acceptedFiles => {
    setCurrentPhoto(acceptedFiles[0])
  }, [])
  const { getRootProps, getInputProps } = useDropzone({ onDrop })

  const [editing, setEditing] = useState(false)

  const submit = async () => {
    try {
      setLoading(true)

      const formData = new FormData()

      formData.append(`user[full_name]`, form.full_name);
      formData.append(`user[username]`, form.username);
      formData.append(`user[email]`, form.email);
      formData.append(`user[phone]`, `+62${form.phone}`);

      if (currentPhoto) {
        formData.append(`user[user_image_attributes[id]]`, imageId);
        formData.append(`user[user_image_attributes[public]]`, currentPhoto);
        formData.append(`user[user_image_attributes[internal]]`, currentPhoto);
      }

      await updateColleague(id, formData);
      window.location.reload()
      onClose()
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const generateQRCode = async () => {
    try {
      const options = {
        color: {
          dark: '#000000', // QR code color
          light: '#ffffff00' // Background color (transparent in this example)
        }
      };
      const url = await QRCode.toDataURL(`${PORTAL_URL}/marketing/${username}`, options);
      setQrCodeUrl(url);
    } catch (err) {
      console.error(err);
    }
  };

  const convertToPdf = async () => {
    try {
      setLoading(true);

      const inputFront = frontCardRef.current;
      const inputBack = backCardRef.current;

      const w = 188;
      const h = 321;

      const scale = 10;
      const canvasOptions = {
        scale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
      };
      const pdfOptions = {
        orientation: 'p',
        unit: 'mm',
        format: [w, h],
      };

      const pdf = new jsPDF(pdfOptions);

      const frontCanvas = await html2canvas(inputFront, canvasOptions);
      const frontImg = frontCanvas.toDataURL('image/png');
      pdf.addImage(frontImg, 'PNG', 0, 0, w, h);

      const backCanvas = await html2canvas(inputBack, canvasOptions);
      const backImg = backCanvas.toDataURL('image/png');
      pdf.addPage(); // Create a new page
      pdf.addImage(backImg, 'PNG', 0, 0, w, h);

      pdf.save(`${form.full_name || 'id-card'}.pdf`);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  const formatCareerLevel = useMemo(() => {
    const role = data?.role?.name ?? "";
    const careerLevel = data?.career_level?.name
    const name = role === "Marketing Associate" ? careerLevel : role;
    if (!name) return "";

    const words = name.split(' ');
    if (words.length <= 2) {
      return name;
    }

    const abbreviatedWords = words.slice(0, 2).concat(words.slice(2).map(word => word.charAt(0) + '.'));
    return abbreviatedWords.join(' ');
  }, [])

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return ""

    if (phoneNumber.startsWith('+62')) {
      phoneNumber = `0${phoneNumber.slice(3)}`
    }

    // Menghapus semua karakter selain angka
    phoneNumber = phoneNumber.replace(/\D/g, '');

    return phoneNumber.replace(/(\d{4})(?=\d)/g, '$1.')
  }

  const disabled = useMemo(() => !editing || loading, [editing, loading])

  useEffect(() => {
    generateQRCode()
  }, []);

  const RenderButtons = useMemo(() => {
    if (editing) {
      return (
        <div className={Styles['btn-wrapper']}>
          <button disabled={loading} onClick={() => setEditing(false)} className={`${Styles['btn']} ${Styles['white']}`}>
            <CrossBlackIcon />
            <span>Batal</span>
          </button>
          <button onClick={submit} className={`${Styles['btn']} ${Styles['black']}`}>
            <DoubleCheck />
            <span>Simpan perubahan</span>
          </button>
        </div>
      )
    }

    return (
      <div className={Styles['btn-wrapper']}>
        <button onClick={() => setEditing(true)} className={`${Styles['btn']} ${Styles['white']}`}>
          <Pencil />
          <span>Ubah</span>
        </button>
      </div>
    )

  }, [editing, form, loading, currentPhoto, imageId, id, onClose])

  const RenderFieldFotoProfile = useMemo(() => {
    if (editing) {
      return (
        <div className={Styles['upload-new-profile-wrapper']}>
          <p className={Styles['title-unggah-foto']}>Unggah foto profil web</p>
          <div {...getRootProps({ className: Styles['dropzone'] })}>
            <Gallery />
            <p className={Styles['text-pilih-foto']}>Pilih foto</p>
            <input {...getInputProps()} />
          </div>
          <p className={Styles['footer-unggah-foto']}>*Foto yang diunggah akan ditampilkan di web pribadi marketing.</p>
        </div>
      )
    }

    // if(userDontHavePhoto) {
    if (data?.user_image?.online_profile?.url === DefaultImage) {
      return (
        <div className={Styles['wrapper-info-profile-web']}>
          <p className={Styles['title-profile-web']}>Foto profil web</p>
          <p className={Styles['desc-profile-web']}>Belum ada foto profil untuk web.</p>
          <p className={Styles['footer-profile-web']}>*Foto profil ini ditampilkan di web pribadi marketing.</p>
        </div>
      )
    }

    // user have photo
    return (
      <div className={Styles['wrapper-info-profile-web']}>
        <p className={Styles['title-profile-web']}>Foto profil web</p>
        <p className={`${Styles['desc-profile-web']} ${Styles['green']}`}>Foto telah diupload.</p>
        <p className={Styles['footer-profile-web']}>*Foto profil ini ditampilkan di web pribadi marketing.</p>
      </div>
    )
  }, [editing, form, loading, currentPhoto, imageId, id, onClose])

  useOutsideAlerter(modalIdCardRef, () => setIsOpenIdCard(false))
  return (
    <Modal toggle={!loading && onClose}>

      <div className={Styles['scrolled-content']}>
        <div className={Styles['wrapper-content']}>
          <div className={Styles['wrapper-close']}>
            <CrossIcon onClick={onClose} />
          </div>

          <p className={Styles['text-detail-marketing']}>Detail Marketing</p>
          {editing ? (
            <div className={Styles['image-wrapper']} {...getRootProps()}>
              <input {...getInputProps()} />
              {currentPhoto ? <img src={URL.createObjectURL(currentPhoto)} alt="..." /> : <img src={image} alt="..." />}
              <button className={Styles['btn-ubah']}><span>Ubah</span></button>
            </div>
          ) : (
            <div className={Styles['image-wrapper']}>
              {currentPhoto ? <img src={URL.createObjectURL(currentPhoto)} alt="..." /> : <img src={image} alt="..." />}
            </div>
          )}

          <Input
            label="Nama Lengkap"
            placeholder="Masukkan Nama Lengkap"
            type="text"
            onChange={onChangeForm}
            value={form.full_name}
            name={'full_name'}
            disabled={disabled}
          />
          <Input
            label="Username"
            placeholder="Masukkan Username"
            type="text"
            onChange={onChangeForm}
            value={form.username}
            name={'username'}
            disabled={true}
          />
          <InputPhone
            label="Nomor Handphone/WhatsApp"
            placeholder="Masukkan Handphone/WhatsApp"
            type="text"
            onChange={onChangeForm}
            value={form.phone}
            name={'phone'}
            disabled={disabled}
          />
          <Input
            label="Alamat Email"
            placeholder="Masukkan Nama Lengkap"
            type="text"
            onChange={onChangeForm}
            value={form.email}
            name={'email'}
            disabled={disabled}
          />
          {RenderFieldFotoProfile}
          <div className={Styles["wrapper-id-card-button"]}>
            <p className={Styles["title-id-card"]}>ID Card</p>
            <Button
              Icon={IdentificationCard}
              text={"Buat Kartu Nama"}
              bg='black'
              onClick={() => setIsOpenIdCard(true)}
              feedback={"inactive"}
            />
          </div>
          {RenderButtons}
        </div>

      </div>

      {loading &&
        <div className={Styles['loader-wrapper']}>
          <Loader />
        </div>
      }

      {isOpenIdCard && <div className='fixed bg-backdrop z-[100002] flex justify-center items-center w-screen h-screen'>
        <div ref={modalIdCardRef} className='p-4 bg-white rounded-md'>
          <div className='mb-4'>
            <p className='text-base mb-[12px]'>Nama Marketing</p>
            <input value={currentName} onChange={(e) => setCurrentName(e.target.value || "")} className='rounded-md py-2 px-4 w-full text-base border-[1px] border-solid border-black-rgba-0' />
          </div>
          <div className='flex'>
            <FrontCardV2
              refID={frontCardRef}
              fullname={currentName}
              companyName={data?.company.name}
              registrationNumber={data?.registration_number}
              role={formatCareerLevel}
              userImage={data?.user_image?.name_card?.url}
            />
            <BackCardV2
              refID={backCardRef}
              fullname={currentName}
              companyName={data?.company.name}
              registrationNumber={data?.registration_number}
              role={formatCareerLevel}
              phone={formatPhoneNumber(data?.phone)}
              street={data?.company?.street}
              qrCodeUrl={qrCodeUrl}
              email={data?.email}
            />
          </div>

          <div className={Styles["wrapper-id-card-button"]}>
            <p className={Styles["title-id-card"]}>Kartu Nama</p>
            <Button
              Icon={IdentificationCard}
              text={"Download Kartu Nama"}
              bg='black'
              onClick={convertToPdf}
              feedback={"inactive"}
            />
          </div>
        </div>
      </div>}

    </Modal>
  )
}

export default DetailMarketing